import React, { useCallback, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import qs from 'qs';
import { selectTimeouts } from 'state/timeouts';
import { selectTenant } from 'state/tenant';
import {
  selectInstantCredit,
  selectInstantCreditTimedOut,
} from 'state/instantCredit';

import useIdleTimer from 'hooks/useIdleTimer';
import useQueryParameters, {
  useSecureQueryParameters,
} from 'hooks/useQueryParameters';
import useNavigationHelpers from 'hooks/useNavigationHelpers';

import useWarning from './useWarning';
import DspTimeoutModalView from './DspTimeoutModalView';

const useWarningTime = (): number => {
  const timeouts = useSelector(selectTimeouts);
  const { instantCreditWarningTime: queryWarningTime } = useQueryParameters();
  const { expirationType, expirationValue } = useSelector(selectInstantCredit);
  const { hideInstantCreditSessionWarning } = useSelector(selectTenant).session;

  let warningTime;

  if (expirationType === 'H' && expirationValue === 1) {
    warningTime = 28 * 60;
  } else if (expirationType !== null && expirationValue !== null) {
    warningTime = 58 * 60;
  }
  return hideInstantCreditSessionWarning
    ? 0
    : queryWarningTime || warningTime || timeouts.instantCreditWarning;
};

const useTimeoutTime = (): number => {
  const timeouts = useSelector(selectTimeouts);
  const { instantCreditTimeoutTime: queryTimeoutTime } = useQueryParameters();
  const { expirationType, expirationValue } = useSelector(selectInstantCredit);
  const { useInstantCreditTspTimeout } = useSelector(selectTenant).session;
  // we only get back H & D, the S & M are for speedier testing
  const multipliers: { [mult: string]: number } = {
    S: 1,
    M: 60,
    H: 60 * 60,
    D: 60 * 60 * 24,
  };

  let timeoutTime = null;
  if (
    useInstantCreditTspTimeout &&
    expirationType &&
    Object.prototype.hasOwnProperty.call(multipliers, expirationType)
  ) {
    // Apparently expirationValue should always have a number in this case
    timeoutTime = (expirationValue as number) * multipliers[expirationType];
  } else {
    if (expirationType === 'H' && expirationValue === 1) {
      timeoutTime = 30 * 60;
    } else {
      timeoutTime = 60 * 60;
    }
  }
  return queryTimeoutTime || timeoutTime || timeouts.instantCreditTimeout;
};

const useMaxExtensions = (): number => {
  const timeouts = useSelector(selectTimeouts);
  const { expirationType, expirationValue } = useSelector(selectInstantCredit);

  let maxExtensions;
  if (
    expirationType === 'H' &&
    (expirationValue === 1 || expirationValue === 2)
  ) {
    maxExtensions = 1;
  } else if (expirationType !== null && expirationValue !== null) {
    maxExtensions = 2;
  }

  return maxExtensions || timeouts.sessionExtensionsMax;
};

const DspTimeoutModal: React.FC = props => {
  const { session: sessionConfig } = useSelector(selectTenant);
  const instantCreditTimedOut = useSelector(selectInstantCreditTimedOut);

  const [warned, extendSession] = useWarning(
    useWarningTime(),
    useMaxExtensions(),
    !sessionConfig.hideInstantCreditSessionWarning,
  );

  const allowedQueryParameters = useSecureQueryParameters();
  const timeoutAfter = useTimeoutTime();
  const { push } = useNavigationHelpers();

  const onTimeout = useCallback(() => {
    push({
      pathname: '/dsp-expired',
      search: qs.stringify(allowedQueryParameters, { addQueryPrefix: true }),
    });
  }, [allowedQueryParameters, push]);

  useLayoutEffect(() => {
    if (instantCreditTimedOut) {
      onTimeout();
    }
  }, [instantCreditTimedOut, onTimeout]);

  useIdleTimer(onTimeout, timeoutAfter * 1000);

  return (
    <DspTimeoutModalView
      warned={warned}
      extendSession={extendSession}
      endSession={onTimeout}
      timeoutTime={timeoutAfter}
    >
      {instantCreditTimedOut ? null : props.children}
    </DspTimeoutModalView>
  );
};

export default DspTimeoutModal;
